import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Plynová zařízení - V1 DRAFT, authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs per the Czech BOZP research (NV 191/2022 Sb.; vyhl. 180/2015
 * §5(2)(e)). Czech-only (LocalizedText cs-CZ). 3 chapters (detekce úniku |
 * větrání | kontrola zápalných zdrojů) + a graded quiz with one question per
 * kind. Answer keys live here (repo-privileged) and in the DB; stripped from the
 * client payload by the API.
 */
export const gasAppliancesProfile: TrainingProfile = {
  slug: 'gas-appliances',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Plynová zařízení',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-detekce',
        title: t('Detekce úniku plynu', 'Gas leak detection'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-detekce',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Detekce úniku plynu', 'Gas leak detection'),
          },
          {
            id: 'p-detekce-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Zemní plyn je sám o sobě bez zápachu, proto se **odorizuje** (látkou THT) tak, aby byl únik rozpoznatelný čichem. Charakteristický zápach po „zkažených vejcích“ je první varovný signál. Únik nikdy nehledej **otevřeným plamenem** - použij detektor hořlavých plynů nebo **mýdlový roztok** na spoje.',
              'Natural gas is itself odourless, so it is **odorised** (with THT) to make a leak detectable by smell. The characteristic "rotten eggs" odour is the first warning sign. Never search for a leak with an **open flame** - use a combustible-gas detector or **soapy water** on the joints.',
            ),
          },
          {
            id: 'anim-unik',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'gasLeak',
            steps: [
              {
                label: t('Zápach plynu', 'Smell of gas'),
                say: t('Únik: nehledej příčinu, jednej.', 'A leak: act, do not investigate.'),
              },
              {
                label: t('Nespínej, nezapaluj', 'No switches, no flame'),
                say: t(
                  'Žádné vypínače, zvonky, mobil ani otevřený oheň.',
                  'No switches, doorbells, phones or open flame.',
                ),
              },
              {
                label: t('Vyvětrej a zavři uzávěr', 'Ventilate and close the valve'),
                say: t(
                  'Otevři okna, zavři hlavní uzávěr, opusť prostor a volej 1239.',
                  'Open the windows, close the main valve, leave and call 1239.',
                ),
              },
            ],
            caption: t(
              'Při zápachu plynu: nespínej a nezapaluj - vyvětrej a zavři hlavní uzávěr.',
              'If you smell gas: do not switch anything or ignite - ventilate and close the main shut-off valve.',
            ),
          },
          {
            id: 'c-detekce-co',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Oxid uhelnatý (CO)** vzniká nedokonalým spalováním a je **bezbarvý, bez zápachu a vysoce toxický** - člověk ho čichem nerozpozná. Proto se používá samostatný **detektor CO**, nezávislý na čichové detekci plynu.',
              '**Carbon monoxide (CO)** is produced by incomplete combustion and is **colourless, odourless and highly toxic** - a person cannot detect it by smell. A separate **CO detector** is therefore used, independent of smell-based gas detection.',
            ),
          },
          {
            id: 'cl-detekce',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Kontrola spojů mýdlovým roztokem nebo detekčním sprejem',
                'Check joints with soapy water or leak-detection spray',
              ),
              t(
                'Funkční detektor hořlavých plynů (zemní plyn / LPG)',
                'A working combustible-gas detector (natural gas / LPG)',
              ),
              t(
                'Funkční detektor oxidu uhelnatého (CO)',
                'A working carbon monoxide (CO) detector',
              ),
              t(
                'Znalost pohotovostní linky pro únik plynu: 1239',
                'Knowing the gas-emergency line: 1239',
              ),
            ],
          },
          {
            id: 'k-detekce',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Plyn hledej čichem a detektorem, **nikdy plamenem**. CO čichem nepoznáš - spoléhej na detektor CO.',
              'Detect gas by smell and with a detector, **never with a flame**. You cannot smell CO - rely on a CO detector.',
            ),
          },
        ],
      },
      {
        id: 'ch-vetrani',
        title: t('Větrání a přívod spalovacího vzduchu', 'Ventilation and combustion-air supply'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-vetrani'],
        blocks: [
          {
            id: 'h-vetrani',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Větrání a přívod vzduchu', 'Ventilation and air supply'),
          },
          {
            id: 'p-vetrani-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Spotřebiče na plyn potřebují **dostatečný přívod spalovacího vzduchu** a prostor musí být **trvale větratelný**. Nedostatek vzduchu vede k nedokonalému spalování a vzniku CO; nahromaděný unikající plyn může vytvořit **výbušnou směs**.',
              'Gas appliances need a **sufficient supply of combustion air** and the space must be **permanently ventilable**. A lack of air leads to incomplete combustion and the formation of CO; accumulated leaking gas can form an **explosive mixture**.',
            ),
          },
          {
            id: 'c-vetrani-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Nikdy nezakrývej ani neutěsňuj **větrací otvory a mřížky**. Odsávací ventilátory a digestoře mohou v místnosti se spotřebičem typu B způsobit **podtlak** a zpětný tah spalin - posuď jejich společný provoz.',
              'Never cover or seal **ventilation openings and grilles**. Extractor fans and cooker hoods can create **negative pressure** and a backdraught of flue gas in a room with a type-B appliance - assess their combined operation.',
            ),
          },
          {
            id: 'p-vetrani-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při podezření na únik **otevři okna a dveře**, vytvoř průvan a prostor opusť. Větrání snižuje koncentraci pod mez výbušnosti, ale samo o sobě **neodstraní zdroj úniku** - ten je nutné uzavřít.',
              'If you suspect a leak, **open windows and doors**, create a draught and leave the area. Ventilation lowers the concentration below the explosive limit, but on its own it **does not remove the source of the leak** - that must be shut off.',
            ),
          },
          {
            id: 'kc-vetrani',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Co je první krok při podezření na únik plynu v místnosti?',
              'What is the first step when you suspect a gas leak in a room?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t('Zapnout odsávací ventilátor', 'Switch on the extractor fan'),
              },
              {
                id: 'kc-b',
                label: t('Otevřít okna a dveře a vyvětrat', 'Open windows and doors and ventilate'),
              },
              {
                id: 'kc-c',
                label: t('Rozsvítit, aby bylo lépe vidět', 'Turn on the light to see better'),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Přirozené větrání nevytváří jiskru. Elektrické spínače ani ventilátory v atmosféře s plynem nezapínej.',
              'Natural ventilation creates no spark. Do not operate electrical switches or fans in a gas-laden atmosphere.',
            ),
          },
        ],
      },
      {
        id: 'ch-zapalne',
        title: t('Kontrola zápalných zdrojů', 'Controlling ignition sources'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-zapalne'],
        blocks: [
          {
            id: 'h-zapalne',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Kontrola zápalných zdrojů', 'Controlling ignition sources'),
          },
          {
            id: 'p-zapalne-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Směs zemního plynu se vzduchem je hořlavá a výbušná již od **dolní meze výbušnosti** (přibližně **5 % objemových**). V atmosféře s unikajícím plynem může jako **zápalný zdroj** posloužit jiskra od elektrického spínače, statická elektřina i otevřený oheň.',
              'A natural-gas-air mixture is flammable and explosive from as low as the **lower explosive limit** (approximately **5 % by volume**). In an atmosphere with leaking gas, a spark from an electrical switch, static electricity or an open flame can act as an **ignition source**.',
            ),
          },
          {
            id: 'c-zapalne-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Nezapínej a nevypínej elektrické spínače**, nepoužívej zvonek, mobilní telefon ani jiný možný zdroj jiskry v prostoru s podezřením na únik. Jiskra postačí k iniciaci výbuchu.',
              '**Do not switch electrical switches on or off**, and do not use the doorbell, a mobile phone or any other possible spark source in an area where a leak is suspected. A single spark is enough to trigger an explosion.',
            ),
          },
          {
            id: 'ord-zapalne',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky při zjištění silného úniku plynu do správného pořadí.',
              'Put the steps for a strong gas leak into the correct order.',
            ),
            items: [
              {
                id: 'op-1',
                label: t(
                  'Vyloučit zápalné zdroje (nezapínat spínače, neškrtat)',
                  'Eliminate ignition sources (no switches, no striking a flame)',
                ),
              },
              {
                id: 'op-2',
                label: t('Uzavřít hlavní uzávěr plynu', 'Close the main gas shut-off valve'),
              },
              { id: 'op-3', label: t('Otevřít okna a vyvětrat', 'Open windows and ventilate') },
              {
                id: 'op-4',
                label: t(
                  'Opustit prostor a přivolat pomoc (1239 / 112)',
                  'Leave the area and call for help (1239 / 112)',
                ),
              },
            ],
          },
          {
            id: 'acc-zapalne',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Proč nesmím v atmosféře s plynem manipulovat se spínači?',
                  'Why must I not operate switches in a gas-laden atmosphere?',
                ),
                a: t(
                  'Při sepnutí kontaktu vzniká jiskra, která může zapálit výbušnou směs plynu se vzduchem.',
                  'When the contact closes, a spark is produced that can ignite the explosive gas-air mixture.',
                ),
              },
              {
                q: t(
                  'Kdo smí pracovat na vyhrazeném plynovém zařízení?',
                  'Who may work on reserved gas equipment?',
                ),
                a: t(
                  'Osoba s platným osvědčením podle NV 191/2022 Sb.; firma musí mít oprávnění od TIČR. Mladiství (16-18 let) jen pod dozorem dle vyhl. 180/2015 §5(2)(e).',
                  'A person holding a valid certificate under Gov. Reg. 191/2022 Coll.; the company must be authorised by TIČR. Young workers (16-18) only under supervision per Decree 180/2015 §5(2)(e).',
                ),
              },
            ],
          },
          {
            id: 'k-zapalne',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Nejprve **vyluč zápalné zdroje**, pak uzavři plyn a vyvětrej. Žádné jiskry - žádný výbuch.',
              'First **eliminate ignition sources**, then shut off the gas and ventilate. No sparks - no explosion.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-lel',
      kind: 'numeric',
      chapterId: 'ch-zapalne',
      prompt: t(
        'Přibližně od jaké koncentrace (v % objemových) tvoří zemní plyn se vzduchem výbušnou směs (dolní mez výbušnosti)?',
        'From approximately what concentration (in % by volume) does natural gas form an explosive mixture with air (the lower explosive limit)?',
      ),
      correct: 5,
      tolerance: 1,
    },
    {
      id: 'q-detekce-single',
      kind: 'single',
      chapterId: 'ch-detekce',
      prompt: t('Jak NIKDY nehledat únik plynu?', 'How must you NEVER search for a gas leak?'),
      choices: [
        { id: 'a', label: t('Detektorem hořlavých plynů', 'With a combustible-gas detector') },
        { id: 'b', label: t('Mýdlovým roztokem na spojích', 'With soapy water on the joints') },
        { id: 'c', label: t('Otevřeným plamenem', 'With an open flame') },
      ],
      correct: 'c',
    },
    {
      id: 'q-co-tf',
      kind: 'truefalse',
      chapterId: 'ch-detekce',
      prompt: t(
        'Oxid uhelnatý (CO) lze spolehlivě rozpoznat čichem podle zápachu.',
        'Carbon monoxide (CO) can be reliably detected by its smell.',
      ),
      correct: false,
    },
    {
      id: 'q-vetrani-multi',
      kind: 'multi',
      chapterId: 'ch-vetrani',
      prompt: t(
        'Co zajišťuje bezpečný provoz plynového spotřebiče? (vyber všechny správné)',
        'What ensures the safe operation of a gas appliance? (select all that apply)',
      ),
      choices: [
        {
          id: 'a',
          label: t(
            'Dostatečný přívod spalovacího vzduchu',
            'A sufficient supply of combustion air',
          ),
        },
        {
          id: 'b',
          label: t(
            'Trvale průchozí větrací otvory',
            'Permanently unobstructed ventilation openings',
          ),
        },
        { id: 'c', label: t('Utěsnění všech větracích mřížek', 'Sealing all ventilation grilles') },
        { id: 'd', label: t('Funkční detektor CO', 'A working CO detector') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-zapalne-order',
      kind: 'ordering',
      chapterId: 'ch-zapalne',
      prompt: t(
        'Seřaď kroky při zjištění silného úniku plynu.',
        'Order the steps for a strong gas leak.',
      ),
      choices: [
        { id: 'a', label: t('Vyloučit zápalné zdroje', 'Eliminate ignition sources') },
        { id: 'b', label: t('Uzavřít hlavní uzávěr plynu', 'Close the main gas shut-off valve') },
        { id: 'c', label: t('Vyvětrat', 'Ventilate') },
        {
          id: 'd',
          label: t('Opustit prostor a přivolat pomoc', 'Leave the area and call for help'),
        },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
